import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface InsightRow {
  readonly decisionInsightId: string;
  readonly insightCode: string;
  readonly title: string;
  readonly insightType: string;
  readonly severity: string;
  readonly confidence: string;
  readonly status: string;
  readonly recommendation: string | null;
  readonly generatedAt: string;
}

interface AnomalyResult {
  readonly isAnomaly: boolean;
  readonly zScore: number | null;
  readonly mean: number;
  readonly stdDev: number;
  readonly threshold: number;
}

const INSIGHT_TYPES = ['RECOMMENDATION', 'ANOMALY_DETECTION', 'EXCEPTION_ALERT', 'PERFORMANCE_ALERT', 'OPTIMIZATION_SUGGESTION', 'EXECUTIVE_SUMMARY'];
const STATUSES = ['NEW', 'ACKNOWLEDGED', 'ACTIONED', 'DISMISSED', 'EXPIRED'];
const SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'];
const CONFIDENCES = ['LOW', 'MEDIUM', 'HIGH'];
const TERMINAL = ['ACTIONED', 'DISMISSED', 'EXPIRED'];

/**
 * M37 — Decision Intelligence Panel (§28 / §13-§14 Decision Intelligence).
 * Register of decision insights (T502) — recommendations, exception /
 * performance alerts, optimisation suggestions and executive summaries — with
 * severity, confidence and the acknowledgement lifecycle
 * (NEW → ACKNOWLEDGED → ACTIONED / DISMISSED). Authors generate insights and run
 * rule-based anomaly detection over a baseline (INSIGHT_MANAGE), and drive the
 * lifecycle (acknowledge / action / dismiss). Filterable by type, severity and
 * status. Tenant isolation is enforced server-side (LAW 4). A 403 surfaces a
 * clear permission message.
 */
export function DecisionIntelligencePanel(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<InsightRow[]>([]);
  const [total, setTotal] = useState(0);
  const [insightType, setInsightType] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Generate form
  const [title, setTitle] = useState('');
  const [newType, setNewType] = useState('RECOMMENDATION');
  const [severity, setSeverity] = useState('INFO');
  const [confidence, setConfidence] = useState('MEDIUM');
  const [recommendation, setRecommendation] = useState('');
  const [busy, setBusy] = useState(false);

  // Anomaly form
  const [anomalyTitle, setAnomalyTitle] = useState('');
  const [baseline, setBaseline] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [anomalyBusy, setAnomalyBusy] = useState(false);
  const [anomalyError, setAnomalyError] = useState<string | null>(null);
  const [anomalyResult, setAnomalyResult] = useState<AnomalyResult | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (insightType) params.set('insightType', insightType);
    if (severityFilter) params.set('severity', severityFilter);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: InsightRow[]; total: number }>(`/v1/bi/insights${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, insightType, severityFilter, status, q, reload]);

  const generate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!title.trim()) { setActionError(t('m37.decision.titleRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: InsightRow }>(`/v1/bi/insights`, {
        method: 'POST',
        body: { title: title.trim(), insightType: newType, severity, confidence, recommendation: recommendation.trim() || undefined },
        token,
      });
      setNotice(t('m37.decision.generated', { code: r.data.insightCode, title: r.data.title }));
      setTitle('');
      setRecommendation('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m37.decision.notAuthGenerate'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const detectAnomaly = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setAnomalyError(null);
    setAnomalyResult(null);
    const points = baseline.split(',').map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n));
    if (points.length < 2) { setAnomalyError(t('m37.decision.baselineRequired')); return; }
    if (!currentValue.trim()) { setAnomalyError(t('m37.decision.currentRequired')); return; }
    setAnomalyBusy(true);
    try {
      const r = await apiRequest<{ data: AnomalyResult }>(`/v1/bi/insights/detect-anomaly`, {
        method: 'POST',
        body: { title: anomalyTitle.trim() || undefined, baseline: points, currentValue: Number(currentValue) },
        token,
      });
      setAnomalyResult(r.data);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setAnomalyError(t('m37.decision.notAuthDetect'));
      } else {
        setAnomalyError((err as Error).message);
      }
    } finally {
      setAnomalyBusy(false);
    }
  };

  const transition = async (id: string, verb: 'acknowledge' | 'action' | 'dismiss'): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusyId(id);
    try {
      await apiRequest<{ data: InsightRow }>(`/v1/bi/insights/${id}/${verb}`, { method: 'POST', token });
      setNotice(t(`m37.decision.transitioned.${verb}`));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m37.decision.notAuthUpdate'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main aria-label="decision-intelligence">
      <h1>{t('m37.decision.title')}</h1>

      <section aria-label="generate-insight">
        <h2>{t('m37.decision.generateTitle')}</h2>
        <form aria-label="insight-form" onSubmit={(e) => void generate(e)}>
          <label>{t('m37.decision.titleLabel')}<input aria-label="field-title" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <label>{t('m37.common.type')}
            <select aria-label="field-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {INSIGHT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.common.severity')}
            <select aria-label="field-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {SEVERITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.common.confidence')}
            <select aria-label="field-confidence" value={confidence} onChange={(e) => setConfidence(e.target.value)}>
              {CONFIDENCES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.decision.recommendation')}<textarea aria-label="field-recommendation" value={recommendation} onChange={(e) => setRecommendation(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-insight" disabled={busy}>{busy ? t('m37.decision.generating') : t('m37.decision.generateBtn')}</Button>
        </form>
      </section>

      <section aria-label="detect-anomaly">
        <h2>{t('m37.decision.detectTitle')}</h2>
        <form aria-label="anomaly-form" onSubmit={(e) => void detectAnomaly(e)}>
          <label>{t('m37.decision.titleLabel')}<input aria-label="anomaly-title" value={anomalyTitle} onChange={(e) => setAnomalyTitle(e.target.value)} /></label>
          <label>{t('m37.decision.baseline')}<input aria-label="anomaly-baseline" value={baseline} onChange={(e) => setBaseline(e.target.value)} placeholder={t('m37.decision.baselinePlaceholder')} /></label>
          <label>{t('m37.decision.currentValue')}<input aria-label="anomaly-current" type="number" value={currentValue} onChange={(e) => setCurrentValue(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-anomaly" disabled={anomalyBusy}>{anomalyBusy ? t('m37.decision.detecting') : t('m37.decision.detectBtn')}</Button>
        </form>
        {anomalyError && <p role="alert" aria-label="anomaly-error">{anomalyError}</p>}
        {anomalyResult && (
          <dl role="status" aria-label="anomaly-result">
            <dt>{t('m37.decision.anomaly')}</dt><dd aria-label="anomaly-flag">{anomalyResult.isAnomaly ? t('common.yes') : t('common.no')}</dd>
            <dt>{t('m37.decision.zscore')}</dt><dd aria-label="anomaly-zscore">{decimal(anomalyResult.zScore)}</dd>
            <dt>{t('m37.decision.mean')}</dt><dd aria-label="anomaly-mean">{anomalyResult.mean}</dd>
            <dt>{t('m37.decision.stddev')}</dt><dd aria-label="anomaly-stddev">{anomalyResult.stdDev}</dd>
            <dt>{t('m37.decision.thresholdSigma')}</dt><dd aria-label="anomaly-threshold">{anomalyResult.threshold}</dd>
          </dl>
        )}
      </section>

      {actionError && <p role="alert" aria-label="action-error">{actionError}</p>}
      {notice && <p role="status" aria-label="notice">{notice}</p>}

      <section aria-label="insight-list">
        <h2>{t('m37.decision.listTitle')}</h2>
        <div className="filters">
          <label>{t('m37.common.type')}
            <select aria-label="filter-type" value={insightType} onChange={(e) => setInsightType(e.target.value)}>
              {['', ...INSIGHT_TYPES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.severity')}
            <select aria-label="filter-severity" value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
              {['', ...SEVERITIES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.status')}
            <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {['', ...STATUSES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.search')}<input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m37.decision.searchPlaceholder')} /></label>
        </div>
        {error && <p role="alert">{error}</p>}
        {loading ? <p>{t('common.loading')}</p> : (
          <>
            <p aria-label="insight-count">{t('m37.decision.count', { count: total })}</p>
            <DataTable
              ariaLabel="insight-table"
              rows={rows}
              rowKey={(r) => r.decisionInsightId}
              columns={[
                { key: 'insightCode', header: t('m37.common.code'), render: (r) => r.insightCode },
                { key: 'title', header: t('m37.decision.titleLabel'), render: (r) => r.title },
                { key: 'insightType', header: t('m37.common.type'), render: (r) => r.insightType },
                { key: 'severity', header: t('m37.common.severity'), render: (r) => r.severity },
                { key: 'confidence', header: t('m37.common.confidence'), render: (r) => decimal(r.confidence) },
                { key: 'status', header: t('m37.common.status'), render: (r) => r.status },
                { key: 'actions', header: t('m37.common.actions'), render: (r) => (
                      <>
                      {!TERMINAL.includes(r.status) && (
                        <>
                          {r.status === 'NEW' && <Button variant="secondary" type="button" aria-label={`acknowledge-${r.decisionInsightId}`} disabled={busyId === r.decisionInsightId} onClick={() => void transition(r.decisionInsightId, 'acknowledge')}>{t('m37.decision.acknowledge')}</Button>}
                          <Button variant="secondary" type="button" aria-label={`action-${r.decisionInsightId}`} disabled={busyId === r.decisionInsightId} onClick={() => void transition(r.decisionInsightId, 'action')}>{t('m37.decision.action')}</Button>
                          <Button variant="secondary" type="button" aria-label={`dismiss-${r.decisionInsightId}`} disabled={busyId === r.decisionInsightId} onClick={() => void transition(r.decisionInsightId, 'dismiss')}>{t('m37.decision.dismiss')}</Button>
                        </>
                      )}
                      </>
                ) },
              ]}
            />
          </>
        )}
      </section>
    </main>
  );
}
